import { joinThreads } from '../applog/applog-helpers.ts'
import { SearchContext } from '../applog/datom-types.ts'
import type { Thread } from '../thread/basic.ts'
import { ArrayEvent, SubscribableArray, SubscribableArrayImpl, Unsubscribe } from './subscribable.ts'

export class QueryNode {
	constructor(
		readonly logsOfThisNode: Thread,
		readonly variables: SearchContext,
		readonly prevNode: QueryNode | null = null,
	) {}
	get record() {
		return this.variables // alias for end-user consumption
	}

	get threadOfTrail() {
		if (!this.prevNode) return this.logsOfThisNode
		return joinThreads([
			this.logsOfThisNode,
			this.prevNode.threadOfTrail,
		])
	}
	get trailLogs() {
		return this.threadOfTrail.applogs
	}
}

/** Shared interface for query results (one-off and live) */
export interface IQueryResult {
	readonly nodes: readonly QueryNode[]
	readonly size: number
	readonly isEmpty: boolean
	readonly records: readonly SearchContext[]
	readonly leafNodeLogs: readonly import('../applog/datom-types').Applog[]
	readonly leafNodeThread: Thread
	readonly threadOfAllTrails: Thread
	readonly thread: Thread
	readonly allApplogs: readonly import('../applog/datom-types').Applog[]
}

/**
 * One-off query result — plain frozen snapshot.
 * No subscribe method. No stale-data risk.
 */
export class QueryResult implements IQueryResult {
	constructor(
		readonly nodes: readonly QueryNode[],
	) {}

	get size() {
		return this.nodes.length
	}
	get isEmpty() {
		return this.nodes.length === 0
	}
	get untrackedSize() {
		return this.nodes.length
	}

	get records(): readonly SearchContext[] {
		return this.nodes.map(({ variables }) => variables)
	}
	get leafNodeThread() {
		return joinThreads(
			this.nodes.map(({ logsOfThisNode: thread }) => thread),
		)
	}
	get leafNodeLogSet() {
		return this.nodes.map(({ logsOfThisNode: thread }) => thread.applogs)
	}
	get leafNodeLogs() {
		return this.nodes.flatMap(({ logsOfThisNode: thread }) => thread.applogs)
	}
	get threadOfAllTrails() {
		return joinThreads(this.nodes.map(node => node.threadOfTrail))
	}
	get thread() {
		return this.threadOfAllTrails // alias
	}
	get allApplogs() {
		return this.threadOfAllTrails.applogs
	}
}

/**
 * Live query result — eagerly activated, always up-to-date.
 *
 * `.nodes` returns the current live view.
 * `.subscribe()` receives future delta events (consistent with current state).
 * Must call `.dispose()` when done to tear down upstream subscriptions.
 */
export class LiveQueryResult implements IQueryResult {
	constructor(
		private _source: SubscribableArray<QueryNode>,
		activate = true,
	) {
		if (activate) {
			// Eagerly activate: subscribe with a no-op to start upstream.
			// Store unsub so dispose() can tear it down.
			this._activationUnsub = this._source.subscribe(() => {})
		}
	}

	private _activationUnsub: Unsubscribe | null = null

	/** Subscribe to node change events. Callback fires on future changes only. */
	subscribe(cb: (event: ArrayEvent<QueryNode>) => void, type?: 'derived' | 'reaction'): Unsubscribe {
		return this._source.subscribe(cb, type)
	}

	/** Current nodes — live view, always up-to-date while not disposed */
	get nodes(): readonly QueryNode[] {
		return this._source.items
	}

	get size() {
		return this._source.length
	}
	get isEmpty() {
		return this._source.length === 0
	}
	get untrackedSize() {
		return this._source.length
	}

	get records(): readonly SearchContext[] {
		return this.nodes.map(({ variables }) => variables)
	}
	get leafNodeThread() {
		return joinThreads(
			this.nodes.map(({ logsOfThisNode: thread }) => thread),
		)
	}
	get leafNodeLogSet() {
		return this.nodes.map(({ logsOfThisNode: thread }) => thread.applogs)
	}
	get leafNodeLogs() {
		return this.nodes.flatMap(({ logsOfThisNode: thread }) => thread.applogs)
	}
	get threadOfAllTrails() {
		return joinThreads(this.nodes.map(node => node.threadOfTrail))
	}
	get thread() {
		return this.threadOfAllTrails // alias
	}
	get allApplogs() {
		return this.threadOfAllTrails.applogs
	}

	dispose() {
		this._activationUnsub?.()
		this._activationUnsub = null
		this._source.dispose()
	}
}
